import * as React from "react"
import { useMutation } from "react-query"
import { useForm } from "react-hook-form"
import { AiOutlineUserAdd } from "react-icons/ai"
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  InputGroup,
  InputLeftAddon,
} from "@chakra-ui/react"

import { fetcher } from "../lib/fetcher"
import { useToast } from "../lib/hooks/useToast"
import { API_URL } from "../lib/config"
import { useOrgs } from "./providers/OrgProvider"
import { Form } from "./Form"
import { Input } from "./Input"
import { Select } from "./Select"

interface InviteUserProps {
  invitationsRefetch: () => void
}

interface InviteUserResponse {
  uuid?: string
  detail?: {
    non_field_errors: [
      {
        message: string
        code: string
      },
    ]
  }
}

interface InviteUserRequest {
  email: string
  role: string
  organization: string
}

export const InviteUser: React.FC<InviteUserProps> = (props) => {
  const toast = useToast()
  const { currentOrgs } = useOrgs()
  const modalProps = useDisclosure()

  const defaultValues = {
    email: "",
    role: "renter",
    organization: `${API_URL}organizations/${currentOrgs[0].uuid}/`,
  }
  const form = useForm({ defaultValues })

  const roleOptions = [
    { label: "Renter", value: "renter" },
    { label: "Fleet Operator", value: "fleet_operator" },
    { label: "Supervisor", value: "supervisor" },
  ]

  const organizationOptions = currentOrgs.map((org) => ({
    label: org.name,
    value: `${API_URL}organizations/${org.uuid}/`,
  }))

  const { mutateAsync: invite, isLoading } = useMutation<InviteUserResponse, unknown, InviteUserRequest>(
    (data) => fetcher("invitations/", { body: JSON.stringify(data), method: "post" }),
  )

  const handleSubmit = async (data: typeof defaultValues) => {
    try {
      const res = await invite({ ...data })
      if (res?.uuid) {
        props.invitationsRefetch()
        modalProps.onClose()
        toast({ description: "Successfully invited user" })
      } else if (res?.detail?.non_field_errors) {
        toast({ description: `Error: ${res.detail?.non_field_errors[0].message}`, status: "error" })
      } else {
        toast({ description: "Error inviting user", status: "error" })
      }
    } catch (error) {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  return (
    <>
      <Button
        leftIcon={<AiOutlineUserAdd />}
        onClick={modalProps.onOpen}
        isDisabled={isLoading}
        isLoading={isLoading}
        variant="outline"
      >
        Invite
      </Button>
      <Modal {...modalProps} isCentered>
        <ModalOverlay zIndex={100}>
          <ModalContent>
            <ModalHeader>Invite user</ModalHeader>
            <ModalCloseButton />
            <ModalBody p={6} pt={0}>
              <Form {...form} onSubmit={handleSubmit}>
                <InputGroup>
                  <InputLeftAddon pl={3} w="120px">
                    Email
                  </InputLeftAddon>
                  <Input
                    type="email"
                    borderTopLeftRadius={0}
                    borderBottomLeftRadius={0}
                    name="email"
                    placeholder="ted.jones@gmail.com"
                    isRequired
                  />
                </InputGroup>
                <InputGroup>
                  <InputLeftAddon pl={3} w="120px">
                    Role
                  </InputLeftAddon>
                  <Select
                    borderTopLeftRadius={0}
                    borderBottomLeftRadius={0}
                    isRequired
                    name="role"
                    options={roleOptions}
                  />
                </InputGroup>
                <InputGroup>
                  <InputLeftAddon pl={3} w="120px">
                    Organization
                  </InputLeftAddon>
                  <Select
                    borderTopLeftRadius={0}
                    borderBottomLeftRadius={0}
                    isRequired
                    name="organization"
                    options={organizationOptions}
                  />
                </InputGroup>
                <Button
                  width="100%"
                  colorScheme="green"
                  type="submit"
                  isDisabled={isLoading}
                  isLoading={isLoading}
                >
                  Invite
                </Button>
              </Form>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    </>
  )
}
